// useAppFilters.js - FIXED VERSION with backend data priority

import { useCallback, useEffect, useMemo, useState } from "react";
import { getAppFilterDefaults } from "../context/envDefaults";
import appApiService from "../services/AppApiService";

const mapLLMFiltersToAppFilters = (llmFilters) => {
	console.log("🔧 Mapping LLM filters to App filters:", llmFilters);

	const fieldMapping = {
		// LLM field -> App filter field
		appStores: "appStore",
		appCategories: "categories",
		deviceTypes: "deviceTypes",
		deviceTypes: "deviceTypes", // ✅ Handle both singular and plural
		geography: "geography",
		country: "geography",
		region: "geography",
		selectedCountries: "geography", // ✅ FIXED: This was missing!
		platform: "platform",
		platforms: "platform", // ✅ Handle both singular and plural
		mediaSubtype: "mediaSubtype",
		developers: "developers",
		publishers: "publishers",
		contentRating: "contentRating",
		languages: "languages",
		features: "features",
		safetyTier: "safetyTier",
		downloadRanges: "downloadRanges",
		appAge: "appAge",
		lastUpdated: "lastUpdated",
		marketSource: "marketSource",
		marketStatus: "marketStatus",
		minRating: "minRating",
		minRatingCount: "minRatingCount",
		isPaid: "isPaid",
		coppa: "coppa",

		// Search terms
		searchTerms: "appNames",
		bundleIds: "bundleIds",

		// Performance metrics - FIXED: All should map to their respective fields
		minViewability: "minViewability",
		visibility: "minViewability",
		viewability: "minViewability",
		minCTR: "minCTR",
		minReach: "minReach",

		// Video placement
		videoPlacement: "videoPlacement",

		// ✅ MISSING MAPPINGS ADDED:
		contentStyle: "contentStyle", // For content style filters
		period: "period", // For time period filters
		customFilters: "customFilters", // For custom performance filters
		uploadedDomains: "uploadedBundles", // Map domains to bundles
		isDirect: "isDirect", // Direct vs programmatic
		limit: "limit", // Result limit
	};

	const mappedFilters = {};

	Object.entries(llmFilters).forEach(([llmField, value]) => {
		const appFilterField = fieldMapping[llmField] || llmField;

		// Skip empty values
		if (Array.isArray(value) && value.length === 0) return;
		if (value === null || value === undefined) return;

		// Special handling for search terms
		if (llmField === "searchTerms" && Array.isArray(value)) {
			const searchTermStrings = value
				.map((term) => (typeof term === "object" ? term.text : term))
				.filter(Boolean);

			if (searchTermStrings.length > 0) {
				mappedFilters["appNames"] = searchTermStrings;
			}
			return;
		}

		// ✅ FIXED: Handle countries/geography mapping properly
		if (
			["selectedCountries", "country", "region", "geography"].includes(llmField)
		) {
			// Ensure we're mapping to the correct field name
			mappedFilters["geography"] = Array.isArray(value) ? value : [value];
			console.log(
				`📍 Mapped ${llmField} -> geography:`,
				mappedFilters["geography"],
			);
			return;
		}

		// ✅ Handle device types properly
		if (["deviceTypes", "deviceTypes"].includes(llmField)) {
			mappedFilters["deviceTypes"] = Array.isArray(value) ? value : [value];
			console.log(
				`📍 Mapped ${llmField} -> deviceTypes:`,
				mappedFilters["deviceTypes"],
			);
			return;
		}

		// ✅ Handle platforms properly
		if (["platform", "platforms"].includes(llmField)) {
			mappedFilters["platform"] = Array.isArray(value) ? value : [value];
			console.log(
				`📍 Mapped ${llmField} -> platform:`,
				mappedFilters["platform"],
			);
			return;
		}

		// ✅ Normalize numeric thresholds like ">90"
		if (
			[
				"minViewability",
				"visibility",
				"viewability",
				"minCTR",
				"minReach",
			].includes(llmField)
		) {
			const numericVal =
				typeof value === "string"
					? parseInt(value.replace(/\D/g, ""), 10) // extract numbers from ">90"
					: value;

			// Map to correct field
			if (["minViewability", "visibility", "viewability"].includes(llmField)) {
				mappedFilters["minViewability"] = numericVal || 0;
				console.log(`📍 Normalized ${llmField} -> minViewability:`, numericVal);
			} else if (llmField === "minCTR") {
				mappedFilters["minCTR"] = numericVal || 0;
				console.log(`📍 Normalized ${llmField} -> minCTR:`, numericVal);
			} else if (llmField === "minReach") {
				mappedFilters["minReach"] = numericVal || 0;
				console.log(`📍 Normalized ${llmField} -> minReach:`, numericVal);
			}
			return;
		}

		// ✅ Handle uploaded domains -> bundles mapping
		if (llmField === "uploadedDomains" && value) {
			mappedFilters["uploadedBundles"] = {
				includeList: value.includeList || [],
				excludeList: value.excludeList || [],
			};
			console.log(
				`📍 Mapped ${llmField} -> uploadedBundles:`,
				mappedFilters["uploadedBundles"],
			);
			return;
		}

		// ✅ Handle custom filters
		if (llmField === "customFilters" && Array.isArray(value)) {
			mappedFilters["customFilters"] = value.map((filter) => ({
				id: filter.id || Date.now(),
				column: filter.column,
				threshold: parseFloat(filter.threshold) || 0,
			}));
			console.log(
				`📍 Mapped ${llmField} -> customFilters:`,
				mappedFilters["customFilters"],
			);
			return;
		}

		// Default mapping
		mappedFilters[appFilterField] = value;
		console.log(`📍 Mapped ${llmField} -> ${appFilterField}:`, value);
	});

	console.log("✅ Final mapped filters:", mappedFilters);
	return mappedFilters;
};

export const useAppFilters = (initialFilters = {}, authToken = null) => {
	// Enhanced default app filters structure
	const defaultFilters = getAppFilterDefaults();

	// State
	const [filters, setFilters] = useState({
		...defaultFilters,
		...initialFilters,
	});

	// Enhanced expanded sections to match new sidebar structure
	const [expandedSections, setExpandedSections] = useState({
		mediaSubtype: false,
		deviceType: false,
		videoPlacement: false,
		geography: false,
		appStore: false,
		marketSource: false,
		marketStatus: false,
		platform: false,
		appStatus: false,
		appType: false,
		coppa: false,
		developer: false,
		appName: false,
		bundleId: false,
		contentRating: false,
		categories: false,
		languages: false,
		features: false,
		safetyTier: false,
		appRating: false,
		downloads: false,
		appAge: false,
		lastUpdated: false,
		relevance: false,
		performance: false,
		publishers: false,
		isDirect: false,
	});

	// FIXED: Enhanced API-fetched filter options with backend data priority
	const [filterOptions, setFilterOptions] = useState({
		// Targeting Filters
		mediaSubtypes: [],
		deviceTypes: [],
		videoPlacementTypes: [],
		countries: [],
		regions: [],

		// App Store Information
		appStores: [],
		platforms: [],
		marketSources: [],
		marketStatuses: [],
		distributionChannels: [],

		// App Metadata
		developers: [],
		appCreators: [],
		appNames: [],
		bundleIds: [],
		contentRatings: [], // FIXED: From backend
		categories: [],
		languages: [],

		// App Features
		features: [],

		// Publishers
		publishers: [],
		popularPublishers: [],

		// FIXED: Rating and download data from backend
		rawRatings: [], // Raw rating values from backend
		rawRatingCounts: [], // Raw rating count values from backend
		rawDownloads: [], // Raw download values from backend

		// Safety and Performance - enhanced with backend data
		safetyTiers: [],
		downloadRanges: [], // Calculated from rawDownloads
		ratingRanges: [], // Calculated from rawRatings
		appAgeRanges: [],
		lastUpdatedRanges: [],

		// Custom filters
		availableColumns: [],

		// Additional data
		domains: [],
		keywords: [],
		origins: [],

		// Status
		loading: true,
		error: null,
	});

	// Set auth token when it changes
	useEffect(() => {
		if (authToken) {
			console.log("Auth token set in AppApiService");
		}
	}, [authToken]);

	// FIXED: Helper function to find closest backend value
	const findClosestValue = useCallback(
		(targetValue, availableValues, type = "number") => {
			if (!availableValues || availableValues.length === 0) return targetValue;

			if (type === "number") {
				const numericValues = availableValues
					.map((v) => parseFloat(v))
					.filter((v) => !isNaN(v))
					.sort((a, b) => a - b);

				if (numericValues.length === 0) return targetValue;

				const target = parseFloat(targetValue);
				if (isNaN(target)) return numericValues[0];

				// Find closest value
				let closest = numericValues[0];
				let minDiff = Math.abs(target - closest);

				for (const value of numericValues) {
					const diff = Math.abs(target - value);
					if (diff < minDiff) {
						minDiff = diff;
						closest = value;
					}
				}

				return closest;
			}

			return targetValue;
		},
		[],
	);

	// FIXED: Enhanced filter values fetch with backend data priority
	const fetchFilterValues = useCallback(async () => {
		try {
			setFilterOptions((prev) => ({ ...prev, loading: true, error: null }));

			console.log("Fetching enhanced app filter values from AppApiService...");

			const response = await appApiService.getFilterValues();

			if (response.success) {
				console.log(
					"Enhanced app filter values received:",
					response.appFilterValues,
				);

				// Extract data from the processed API response
				const filterData = response.appFilterValues || {};

				// FIXED: Process backend rating and download data
				const processRatingData = (ratings) => {
					if (!Array.isArray(ratings)) return { ranges: [], raw: [] };

					const numericRatings = ratings
						.map((r) => parseFloat(r))
						.filter((r) => !isNaN(r) && r >= 0 && r <= 5)
						.sort((a, b) => a - b);

					const ranges = new Set();
					numericRatings.forEach((rating) => {
						if (rating >= 4.5) ranges.add("4.5-5.0");
						else if (rating >= 4.0) ranges.add("4.0-4.5");
						else if (rating >= 3.5) ranges.add("3.5-4.0");
						else if (rating >= 3.0) ranges.add("3.0-3.5");
						else if (rating >= 2.5) ranges.add("2.5-3.0");
						else if (rating >= 2.0) ranges.add("2.0-2.5");
						else if (rating >= 1.5) ranges.add("1.5-2.0");
						else ranges.add("1.0-1.5");
					});

					return {
						ranges: Array.from(ranges).sort(),
						raw: numericRatings,
					};
				};

				const processRatingCountData = (ratingCounts) => {
					if (!Array.isArray(ratingCounts)) return [];

					return ratingCounts
						.map((rc) => parseInt(rc))
						.filter((rc) => !isNaN(rc) && rc >= 0)
						.sort((a, b) => a - b);
				};

				const processDownloadData = (downloads) => {
					if (!Array.isArray(downloads)) return { ranges: [], raw: [] };

					const numericDownloads = downloads
						.map((d) => parseInt(d))
						.filter((d) => !isNaN(d) && d >= 0)
						.sort((a, b) => a - b);

					const ranges = new Set();
					numericDownloads.forEach((download) => {
						if (download >= 100000000) ranges.add("100M+");
						else if (download >= 50000000) ranges.add("50M-100M");
						else if (download >= 10000000) ranges.add("10M-50M");
						else if (download >= 1000000) ranges.add("1M-10M");
						else if (download >= 100000) ranges.add("100K-1M");
						else if (download >= 50000) ranges.add("50K-100K");
						else if (download >= 10000) ranges.add("10K-50K");
						else if (download >= 1000) ranges.add("1K-10K");
						else if (download >= 100) ranges.add("100-1K");
						else ranges.add("1-100");
					});

					return {
						ranges: Array.from(ranges).sort(),
						raw: numericDownloads,
					};
				};

				// FIXED: Process backend data with proper fallbacks
				const ratingData = processRatingData(filterData.ratings || []);
				const ratingCountData = processRatingCountData(
					filterData.ratingCounts || [],
				);
				const downloadData = processDownloadData(filterData.downloads || []);

				setFilterOptions({
					// Targeting Filters
					mediaSubtypes: filterData.mediaSubtypes || [
						"Display",
						"Video",
						"Native",
					],
					deviceTypes: filterData.deviceTypes || ["phone", "tablet", "desktop"],
					videoPlacementTypes: filterData.videoPlacementTypes || [
						"INSTREAM",
						"OUTSTREAM",
					],
					countries: filterData.countries || ["US", "GB", "CA"],
					regions: filterData.regions || [],

					// App Store Information
					appStores: filterData.appStores || [
						"Google Play Store",
						"Apple App Store",
					],
					platforms: filterData.platforms || ["android", "ios"],
					marketSources: filterData.marketSources || ["playstore", "itunes"],
					marketStatuses: filterData.marketStatuses || ["PUBLISHED", "pending"],
					distributionChannels: filterData.distributionChannels || [],

					// App Metadata - FIXED: Use backend data first
					developers: filterData.developers || [],
					appCreators: filterData.developers || [],
					appNames: filterData.appNames || [],
					bundleIds: filterData.bundleIds || [],
					contentRatings: filterData.contentRatings || [
						// FIXED: Backend first, then fallback
						"4+",
						"+12",
						"Teen",
						"General",
						"Medium Maturity",
						"Everyone",
						"Everyone 10+",
						"Mature 17+",
						"Guidance Suggested",
						"Restricted",
					],
					categories: filterData.categories || [],
					languages: filterData.languages || [],

					// App Features
					features: filterData.features || [],

					// Publishers
					publishers: filterData.publishers || [],
					popularPublishers: filterData.publishers || [],

					// FIXED: Backend rating and download data
					rawRatings: ratingData.raw,
					rawRatingCounts: ratingCountData,
					rawDownloads: downloadData.raw,

					// Safety and Performance - FIXED: Use backend data
					safetyTiers: filterData.safetyTiers || [
						"Universal",
						"Standard",
						"Limited",
					],
					downloadRanges:
						downloadData.ranges.length > 0
							? downloadData.ranges
							: [
									"1-100",
									"100-1K",
									"1K-10K",
									"10K-50K",
									"50K-100K",
									"100K-1M",
									"1M-10M",
									"10M-50M",
									"50M-100M",
									"100M+",
								],
					ratingRanges:
						ratingData.ranges.length > 0
							? ratingData.ranges
							: [
									"1.0-1.5",
									"1.5-2.0",
									"2.0-2.5",
									"2.5-3.0",
									"3.0-3.5",
									"3.5-4.0",
									"4.0-4.5",
									"4.5-5.0",
								],
					appAgeRanges: filterData.appAgeRanges || [],
					lastUpdatedRanges: filterData.lastUpdatedRanges || [],

					// Custom filters
					availableColumns: filterData.availableColumns || [],

					// Additional data
					domains: filterData.domains || [],
					keywords: filterData.keywords || [],
					origins: filterData.origins || [],

					loading: false,
					error: null,
				});

				// FIXED: Update filter defaults based on backend data
				setFilters((prevFilters) => {
					const updatedFilters = { ...prevFilters };

					// Set default rating if backend has data and no current selection
					if (
						ratingData.raw.length > 0 &&
						(prevFilters.minRating === null ||
							prevFilters.minRating === undefined)
					) {
						// Set to a reasonable default (like 3.0 or closest available)
						updatedFilters.minRating = findClosestValue(
							3.0,
							ratingData.raw,
							"number",
						);
					}

					// Set default rating count if backend has data and no current selection
					if (ratingCountData.length > 0 && prevFilters.minRatingCount === 0) {
						// Set to a reasonable default (like 100 or closest available)
						updatedFilters.minRatingCount = findClosestValue(
							100,
							ratingCountData,
							"number",
						);
					}

					return updatedFilters;
				});

				console.log(
					"App filter options loaded from API with backend data priority:",
					{
						categoriesCount: filterData.categories?.length || 0,
						developersCount: filterData.developers?.length || 0,
						publishersCount: filterData.publishers?.length || 0,
						platformsCount: filterData.platforms?.length || 0,
						contentRatingsCount: filterData.contentRatings?.length || 0,
						countriesCount: filterData.countries?.length || 0,
						availableColumnsCount: filterData.availableColumns?.length || 0,
						backendRatings: ratingData.raw.length,
						backendRatingCounts: ratingCountData.length,
						backendDownloads: downloadData.raw.length,
						isFallback: response.fallback || false,
					},
				);

				if (response.fallback) {
					console.warn(
						"Using fallback filter data due to API issues:",
						response.error,
					);
				}
			} else {
				throw new Error(response.error || "Failed to fetch app filter values");
			}
		} catch (error) {
			console.error("Failed to fetch app filter values:", error);

			// Set fallback data instead of leaving empty
			const fallbackData = appApiService.getDefaultAppFilterValues();

			setFilterOptions({
				mediaSubtypes: fallbackData.mediaSubtypes || [],
				deviceTypes: fallbackData.deviceTypes || [],
				videoPlacementTypes: fallbackData.videoPlacementTypes || [],
				countries: fallbackData.countries || [],
				regions: fallbackData.regions || [],
				appStores: fallbackData.appStores || [],
				platforms: fallbackData.platforms || [],
				marketSources: fallbackData.marketSources || [],
				marketStatuses: fallbackData.marketStatuses || [],
				distributionChannels: fallbackData.distributionChannels || [],
				developers: fallbackData.developers || [],
				appCreators: fallbackData.developers || [],
				appNames: fallbackData.appNames || [],
				bundleIds: fallbackData.bundleIds || [],
				contentRatings: fallbackData.contentRatings || [],
				categories: fallbackData.categories || [],
				languages: fallbackData.languages || [],
				features: [],
				publishers: fallbackData.publishers || [],
				popularPublishers: fallbackData.publishers || [],
				rawRatings: [],
				rawRatingCounts: [],
				rawDownloads: [],
				safetyTiers: [],
				downloadRanges: fallbackData.downloadRanges || [],
				ratingRanges: fallbackData.ratingRanges || [],
				appAgeRanges: [],
				lastUpdatedRanges: [],
				availableColumns: fallbackData.availableColumns || [],
				domains: fallbackData.domains || [],
				keywords: fallbackData.keywords || [],
				origins: fallbackData.origins || [],
				loading: false,
				error: error.message || "Failed to load filter options",
			});
		}
	}, [findClosestValue]);

	// Load filter values on mount and when auth token changes
	useEffect(() => {
		fetchFilterValues();
	}, [fetchFilterValues]);

	// Update a single filter value
	const updateFilter = useCallback((key, value) => {
		setFilters((prev) => {
			const updated = { ...prev, [key]: value };
			console.log(`App filter updated: ${key} =`, value);
			return updated;
		});
	}, []);

	// FIXED: Smart rating update that uses closest backend value
	const updateRatingFilter = useCallback(
		(key, value) => {
			setFilters((prev) => {
				let finalValue = value;

				if (key === "minRating" && filterOptions.rawRatings.length > 0) {
					finalValue = findClosestValue(
						value,
						filterOptions.rawRatings,
						"number",
					);
					console.log(
						`Rating filter adjusted from ${value} to closest backend value ${finalValue}`,
					);
				} else if (
					key === "minRatingCount" &&
					filterOptions.rawRatingCounts.length > 0
				) {
					finalValue = findClosestValue(
						value,
						filterOptions.rawRatingCounts,
						"number",
					);
					console.log(
						`Rating count filter adjusted from ${value} to closest backend value ${finalValue}`,
					);
				}

				const updated = { ...prev, [key]: finalValue };
				console.log(`Smart filter updated: ${key} = ${finalValue}`);
				return updated;
			});
		},
		[filterOptions.rawRatings, filterOptions.rawRatingCounts, findClosestValue],
	);

	// Toggle array filter (add/remove from array)
	const toggleArrayFilter = useCallback((key, value) => {
		setFilters((prev) => {
			const currentArray = prev[key] || [];
			const newArray = currentArray.includes(value)
				? currentArray.filter((item) => item !== value)
				: [...currentArray, value];

			const updated = { ...prev, [key]: newArray };
			console.log(`App array filter toggled: ${key} =`, newArray);
			return updated;
		});
	}, []);

	// Toggle section expansion
	const toggleSection = useCallback((section, forceState = null) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: forceState !== null ? forceState : !prev[section],
		}));
	}, []);

	// Enhanced custom filters management
	const addCustomFilter = useCallback(() => {
		const currentFilters = filters.customFilters || [];
		const newFilter = {
			id: Date.now() + Math.random(),
			column: "cprmarketrequests",
			threshold: 0,
		};
		updateFilter("customFilters", [...currentFilters, newFilter]);
		console.log("Added custom filter:", newFilter);
	}, [filters.customFilters, updateFilter]);

	const updateCustomFilter = useCallback(
		(id, field, value) => {
			const currentFilters = filters.customFilters || [];
			const updatedFilters = currentFilters.map((filter) =>
				filter.id === id ? { ...filter, [field]: value } : filter,
			);
			updateFilter("customFilters", updatedFilters);
			console.log("Updated custom filter:", id, field, value);
		},
		[filters.customFilters, updateFilter],
	);

	const removeCustomFilter = useCallback(
		(id) => {
			const currentFilters = filters.customFilters || [];
			const updatedFilters = currentFilters.filter(
				(filter) => filter.id !== id,
			);
			updateFilter("customFilters", updatedFilters);
			console.log("Removed custom filter:", id);
		},
		[filters.customFilters, updateFilter],
	);

	// Clear all filters
	const clearAllFilters = useCallback(() => {
		console.log("Clearing all app filters");
		setFilters({
			...defaultFilters,
			mediaSubtype: [],
			deviceTypes: [],
			geography: [],
			limit: 1000,
			customFilters: [
				{
					id: Date.now(),
					column: "cprmarketrequests",
					threshold: 0,
				},
				{
					id: Date.now() + 1,
					column: "impressions",
					threshold: 0,
				},
			],
		});
		setExpandedSections({});
	}, []);

	// Reset to initial filters
	const resetFilters = useCallback(() => {
		setFilters({
			...defaultFilters,
			...initialFilters,
		});
	}, [initialFilters]);

	// Enhanced active filter count calculation
	const getActiveFilterCount = useMemo(() => {
		let count = 0;

		const arrayFilters = [
			"mediaSubtype",
			"deviceTypes",
			"videoPlacement",
			"geography",
			"appStore",
			"marketSource",
			"marketStatus",
			"platform",
			"appStatus",
			"contentRating",
			"categories",
			"languages",
			"features",
			"safetyTier",
			"downloadRanges",
			"appAge",
			"lastUpdated",
			"developers",
			"publishers",
		];

		arrayFilters.forEach((key) => {
			if (filters[key]?.length > 0) count++;
		});

		if (filters.appNames?.length > 0) count++;
		if (filters.bundleIds?.length > 0) count++;
		if (filters.isPaid !== null) count++;
		if (filters.coppa !== null) count++;
		if (filters.minRating !== null) count++;
		if (filters.minRatingCount > 0) count++;
		if (filters.minViewability > 0) count++;
		if (filters.minCTR > 0) count++;
		if (filters.minReach > 0) count++;
		if (filters.customFilters?.length > 2) count++;
		if (filters.uploadedBundles?.includeList?.length > 0) count++;
		if (filters.uploadedBundles?.excludeList?.length > 0) count++;

		return count;
	}, [filters]);

	// FIXED: Enhanced API filters conversion with correct field mapping
	const getApiFilters = useCallback(() => {
		console.log("Converting UI filters to API format:", filters);

		const apiFilters = {
			// Targeting - FIXED field mappings
			mediaSubtype: filters.mediaSubtype,
			deviceTypes: filters.deviceTypes,
			videoplcmt: filters.videoPlacement,
			geography: filters.geography,

			// App Store - FIXED field mappings
			marketSource: filters.marketSource,
			marketStatus: filters.marketStatus,
			platform: filters.platform,
			isPaid: filters.isPaid,
			coppa: filters.coppa,

			// App Metadata - FIXED field mappings
			developer: filters.developers,
			appCreators: filters.appCreators,
			appNames: filters.appNames,
			bundleIds: filters.bundleIds,
			contentRating: filters.contentRating,
			category: filters.categories,
			languages: filters.languages,

			// App Features
			features: filters.features,

			// Publishers
			publishers: filters.publishers,
			isDirect: filters.isDirect,

			// App Performance - FIXED field mappings
			safetyTier: filters.safetyTier,
			rating: filters.minRating,
			rating_count: filters.minRatingCount,
			downloads: filters.downloadRanges,

			// App Lifecycle
			appAge: filters.appAge,
			lastUpdated: filters.lastUpdated,

			// Performance Metrics
			relevance: filters.relevance,
			minViewability: filters.minViewability,
			minCTR: filters.minCTR,
			minReach: filters.minReach,

			// Custom filters for API columns
			customFilters: filters.customFilters,

			// Upload domain filters
			uploadedBundles: filters.uploadedBundles,

			// Query options - FIXED mapping
			noOfResult: filters.limit,
			limit: filters.limit,
			sortBy: filters.sortBy,
			sortOrder: filters.sortOrder,
		};

		// Remove empty arrays and null values, but keep explicitly set values
		Object.keys(apiFilters).forEach((key) => {
			if (Array.isArray(apiFilters[key]) && apiFilters[key].length === 0) {
				delete apiFilters[key];
			} else if (apiFilters[key] === undefined) {
				delete apiFilters[key];
			}
		});

		console.log(
			"API filters after conversion with backend data integration:",
			apiFilters,
		);
		return apiFilters;
	}, [filters]);

	// Enhanced filter summary for display
	const getFilterSummary = useMemo(() => {
		const summary = [];

		if (filters.geography?.length > 0) {
			summary.push(`${filters.geography.length} countries`);
		}

		if (filters.categories?.length > 0) {
			summary.push(`${filters.categories.length} categories`);
		}

		if (filters.developers?.length > 0) {
			summary.push(`${filters.developers.length} developers`);
		}

		if (filters.publishers?.length > 0) {
			summary.push(`${filters.publishers.length} publishers`);
		}

		if (filters.platform?.length > 0) {
			summary.push(`${filters.platform.join(", ")} platform`);
		}

		if (filters.marketSource?.length > 0) {
			summary.push(`${filters.marketSource.join(", ")} sources`);
		}

		if (filters.minRating !== null) {
			summary.push(`${filters.minRating}+ rating`);
		}

		if (filters.downloadRanges?.length > 0) {
			summary.push(`${filters.downloadRanges.length} download ranges`);
		}

		if (filters.customFilters?.length > 0) {
			summary.push(`${filters.customFilters.length} custom filters`);
		}

		if (filters.uploadedBundles?.includeList?.length > 0) {
			summary.push(
				`${filters.uploadedBundles.includeList.length} allowed Bundles`,
			);
		}

		if (filters.uploadedBundles?.excludeList?.length > 0) {
			summary.push(
				`${filters.uploadedBundles.excludeList.length} blocked Bundles`,
			);
		}

		return summary;
	}, [filters]);

	// Enhanced validation with custom filters
	const validateFilters = useCallback(() => {
		const errors = [];

		if (filters.minViewability < 0 || filters.minViewability > 100) {
			errors.push("Viewability must be between 0 and 100");
		}

		if (filters.minCTR < 0 || filters.minCTR > 100) {
			errors.push("CTR must be between 0 and 100");
		}

		if (
			filters.relevance &&
			(filters.relevance < 0 || filters.relevance > 100)
		) {
			errors.push("Relevance must be between 0 and 100");
		}

		// Validate custom filters
		if (filters.customFilters?.length > 0) {
			filters.customFilters.forEach((customFilter, index) => {
				if (!customFilter.column) {
					errors.push(`Custom filter ${index + 1}: Column is required`);
				}
				if (
					customFilter.threshold === null ||
					customFilter.threshold === undefined
				) {
					errors.push(`Custom filter ${index + 1}: Threshold is required`);
				}
				if (
					typeof customFilter.threshold === "number" &&
					customFilter.threshold < 0
				) {
					errors.push(
						`Custom filter ${index + 1}: Threshold must be non-negative`,
					);
				}
			});
		}

		return {
			isValid: errors.length === 0,
			errors,
		};
	}, [filters]);

	// Enhanced preset filters
	const applyPreset = useCallback(
		(presetName) => {
			const presets = {
				popular: {
					minRating: findClosestValue(4.0, filterOptions.rawRatings, "number"),
					minRatingCount: findClosestValue(
						1000,
						filterOptions.rawRatingCounts,
						"number",
					),
					downloadRanges: ["1M-10M", "10M-50M", "50M-100M", "100M+"],
					categories: ["Entertainment", "Games", "Social Networking"],
					safetyTier: ["Universal", "Standard"],
				},
				gaming: {
					categories: ["Games", "Role Playing", "Adventure", "Puzzle"],
					minRating: findClosestValue(3.5, filterOptions.rawRatings, "number"),
					downloadRanges: ["100K-1M", "1M-10M", "10M-50M", "50M-100M", "100M+"],
					features: ["In-App Purchases"],
				},
				productivity: {
					categories: ["Business", "Productivity", "Utilities"],
					minRating: findClosestValue(4.0, filterOptions.rawRatings, "number"),
					features: ["Push Notifications", "Offline Viewing"],
					safetyTier: ["Universal", "Standard"],
				},
				social: {
					categories: ["Social Networking", "Communication", "Photo & Video"],
					features: ["Users Interact", "Social Features", "Camera Access"],
					minRating: findClosestValue(3.5, filterOptions.rawRatings, "number"),
				},
				news: {
					categories: ["News & Magazines"],
					minRating: findClosestValue(3.8, filterOptions.rawRatings, "number"),
					features: ["Push Notifications"],
					publishers: filterOptions.popularPublishers?.slice(0, 5) || [],
				},
				health: {
					categories: ["Health & Fitness"],
					minRating: findClosestValue(4.0, filterOptions.rawRatings, "number"),
					features: ["Push Notifications", "Shares Location"],

					safetyTier: ["Universal", "Standard"],
				},
				highPerformance: {
					minViewability: 85,
					minCTR: 2.5,
					relevance: 90,
					minReach: 5,
					minRating: findClosestValue(4.2, filterOptions.rawRatings, "number"),
				},
			};

			const preset = presets[presetName];
			if (preset) {
				console.log(
					`Applying enhanced app filter preset: ${presetName} with backend-adjusted values`,
				);
				setFilters((prev) => ({ ...prev, ...preset }));
			}
		},
		[
			filterOptions.popularPublishers,
			filterOptions.rawRatings,
			filterOptions.rawRatingCounts,
			findClosestValue,
		],
	);

	// Simple retry method
	const retryFetchFilterOptions = useCallback(() => {
		console.log("Retrying app filter fetch...");
		fetchFilterValues();
	}, [fetchFilterValues]);

	// Bundle management methods
	const addToIncludeList = useCallback(
		(bundleId) => {
			const current = filters.uploadedBundles || {
				includeList: [],
				excludeList: [],
			};
			const newIncludeList = [...current.includeList, bundleId];

			updateFilter("uploadedBundles", {
				...current,
				includeList: [...new Set(newIncludeList)],
			});
		},
		[filters.uploadedBundles, updateFilter],
	);

	const addToExcludeList = useCallback(
		(bundleId) => {
			const current = filters.uploadedBundles || {
				includeList: [],
				excludeList: [],
			};
			const newExcludeList = [...current.excludeList, bundleId];

			updateFilter("uploadedBundles", {
				...current,
				excludeList: [...new Set(newExcludeList)],
			});
		},
		[filters.uploadedBundles, updateFilter],
	);

	const removeFromIncludeList = useCallback(
		(bundleId) => {
			const current = filters.uploadedBundles || {
				includeList: [],
				excludeList: [],
			};

			updateFilter("uploadedBundles", {
				...current,
				includeList: current.includeList.filter((id) => id !== bundleId),
			});
		},
		[filters.uploadedBundles, updateFilter],
	);

	const removeFromExcludeList = useCallback(
		(bundleId) => {
			const current = filters.uploadedBundles || {
				includeList: [],
				excludeList: [],
			};

			updateFilter("uploadedBundles", {
				...current,
				excludeList: current.excludeList.filter((id) => id !== bundleId),
			});
		},
		[filters.uploadedBundles, updateFilter],
	);

	// // Log filter changes for debugging
	// useEffect(() => {
	//   if (process.env.NODE_ENV === 'development') {
	//     console.log('Enhanced app filters updated with backend data:', {
	//       activeCount: getActiveFilterCount,
	//       summary: getFilterSummary,
	//       apiFilters: getApiFilters(),
	//       hasCustomFilters: filters.customFilters?.length > 0,
	//       hasPublishers: filters.publishers?.length > 0,
	//       hasAuthToken: !!authToken,
	//       apiDataLoaded: !filterOptions.loading,
	//       availableColumns: filterOptions.availableColumns?.length || 0,
	//       backendRatingsAvailable: filterOptions.rawRatings?.length || 0,
	//       backendRatingCountsAvailable: filterOptions.rawRatingCounts?.length || 0,
	//       currentMinRating: filters.minRating,
	//       currentMinRatingCount: filters.minRatingCount
	//     });
	//   }
	// }, [filters, getActiveFilterCount, getFilterSummary, getApiFilters, authToken, filterOptions]);

	return {
		// State
		filters,
		expandedSections,
		filterOptions,

		// Actions
		updateFilter,
		updateRatingFilter, // FIXED: New method for rating updates
		toggleArrayFilter,
		toggleSection,
		clearAllFilters,
		resetFilters,
		applyPreset,
		retryFetchFilterOptions,

		// Custom filters management
		addCustomFilter,
		updateCustomFilter,
		removeCustomFilter,

		// Bundle management methods
		addToIncludeList,
		addToExcludeList,
		removeFromIncludeList,
		removeFromExcludeList,

		// Computed values
		activeFilterCount: getActiveFilterCount,
		filterSummary: getFilterSummary,
		apiFilters: getApiFilters(),
		validation: validateFilters(),

		// Enhanced status
		hasActiveFilters: getActiveFilterCount > 0,
		isDefaultState: getActiveFilterCount === 0,
		isLoading: filterOptions.loading,
		hasError: !!filterOptions.error,
		error: filterOptions.error,
		hasCustomFilters: filters.customFilters?.length > 0,
		hasPublishers: filters.publishers?.length > 0,
		customFiltersSupported: filterOptions.availableColumns?.length > 0,

		// Export mapping function for use by other components
		mapLLMFiltersToAppFilters,
	};
};
